import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, X, AlertTriangle } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';

export default function TrialCountdownBanner() {
  const { subscription, isLoading } = useSubscription();
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (subscription && subscription.status === 'trialing' && subscription.current_period_end) {
      const endDate = new Date(subscription.current_period_end);
      const now = new Date();
      const diffTime = endDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysRemaining(Math.max(0, diffDays));
    }
  }, [subscription]);

  // Don't show banner if loading, not trialing, dismissed, or no days remaining
  if (isLoading || !subscription || subscription.status !== 'trialing' || isDismissed || daysRemaining === null || daysRemaining <= 0) {
    return null;
  }

  const getBannerColor = () => {
    if (daysRemaining <= 3) return 'bg-red-50 border-red-200 text-red-800';
    if (daysRemaining <= 7) return 'bg-orange-50 border-orange-200 text-orange-800';
    return 'bg-blue-50 border-blue-200 text-blue-800';
  };

  const getIcon = () => {
    if (daysRemaining <= 3) return <AlertTriangle className="w-5 h-5" />;
    return <Clock className="w-5 h-5" />;
  };

  const getMessage = () => {
    if (daysRemaining === 1) {
      return "Your free trial ends tomorrow!";
    } else if (daysRemaining === 0) {
      return "Your free trial has ended.";
    } else {
      return `Your free trial ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}.`;
    }
  };

  return (
    <div className={`${getBannerColor()} border-l-4 px-4 py-3 mb-4 relative`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {getIcon()}
          <div>
            <p className="font-medium">{getMessage()}</p>
            <p className="text-sm opacity-90">
              Upgrade now to continue using all features without interruption.
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to="/subscription"
            className="bg-white hover:bg-gray-50 px-4 py-2 rounded-md text-sm font-medium border border-current transition-colors"
          >
            Upgrade Now
          </Link>
          <button
            onClick={() => setIsDismissed(true)}
            className="text-current hover:opacity-70 transition-opacity"
            aria-label="Dismiss banner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
